// src\app\services\global\global.service.ts


import { Injectable } from '@angular/core';
import {
  ActionSheetController,
  AlertController,
  LoadingController,
  ModalController,
  ToastController,
} from '@ionic/angular/standalone';
import { isPlatform } from '@ionic/angular/standalone';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { CallNumber } from 'capacitor-call-number';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';

import { NativeAudio } from '@capacitor-community/native-audio';

export type ConfirmOptions = {
  header?: string;
  message: string;
  okText?: string;
  cancelText?: string;
  backdropDismiss?: boolean; // default: false (safer)
};

@Injectable({
  providedIn: 'root',
})
export class GlobalService {
  isLoading: boolean = false;

  constructor(
    private router: Router,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private loadingCtrl: LoadingController,
    private modalCtrl: ModalController,
    private actionSheetCtrl: ActionSheetController,
    private datePipe: DatePipe
  ) {}

  setLoader() {
    this.isLoading = !this.isLoading;
  }

  showAlert(
    message: string,
    header?,
    buttonArray?,
    inputs?,
    backdropDismiss = true
  ) {
    this.alertCtrl
      .create({
        header: header ? header : 'Authentication failed',
        message: message,
        inputs: inputs ? inputs : [],
        buttons: buttonArray ? buttonArray : ['Okay'],
        backdropDismiss: backdropDismiss,
      })
      .then((alertEl) => alertEl.present());
  }

  /**
   * ✅ Minimal confirm helper
   * Returns true if user pressed OK, false if cancelled/dismissed.
   */
  async confirm(options: ConfirmOptions): Promise<boolean> {
    const alert = await this.alertCtrl.create({
      header: options.header || 'تأكيد',
      message: options.message,
      backdropDismiss: options.backdropDismiss ?? false,
      buttons: [
        {
          text: options.cancelText || 'إلغاء',
          role: 'cancel',
        },
        {
          text: options.okText || 'موافق',
          role: 'confirm',
        },
      ],
    });

    await alert.present();
    const res = await alert.onDidDismiss();
    return res.role === 'confirm';
  }

  checkErrorMessageForAlert(error, msg?) {
    if (error?.error?.message) {
      msg = error.error.message;
    }
    console.log('error message alert: ', msg);
    this.showAlert(msg);
  }

  async showActionSheet(buttonArray, header?) {
    const actionSheet = await this.actionSheetCtrl.create({
      header: header ? header : 'Options',
      buttons: buttonArray,
    });
    await actionSheet.present();
  }

  async showToast(msg, color, position, duration = 3000) {
    const toast = await this.toastCtrl.create({
      message: msg,
      duration: duration,
      color: color,
      position: position,
    });
    toast.present();
  }

  errorToast(msg?, duration = 4000) {
    this.showToast(
      msg
        ? msg
        : 'حدث خطأ ما. يرجى التحقق من الاتصال بالانترنت أو إعادة تشغيل التطبيق',
      'danger',
      'bottom',
      duration
    );
  }

  checkMessageForErrorToast(error, msg?) {
    if (error?.error?.message) {
      msg = error.error.message;
    }
    this.errorToast(msg);
  }

  successToast(msg) {
    this.showToast(msg, 'success', 'bottom');
  }

  showLoader(msg?, spinner?) {
    if (!this.isLoading) this.setLoader();
    return this.loadingCtrl
      .create({
        message: msg,
        spinner: spinner ? spinner : 'bubbles',
      })
      .then((res) => {
        res.present().then(() => {
          if (!this.isLoading) {
            res.dismiss().then(() => {});
          }
        });
      })
      .catch((e) => {
        console.log('show loading error: ', e);
      });
  }

  hideLoader() {
    if (this.isLoading) this.setLoader();
    return this.loadingCtrl
      .dismiss()
      .then(() => console.log('dismissed'))
      .catch((e) => console.log('error hide loader: ', e));
  }

  async createModal(options) {
    const modal = await this.modalCtrl.create(options);
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data) return data;
  }

 async modalDismiss(val?: any): Promise<boolean> {
  const data: any = val ?? null;
  try {
    return await this.modalCtrl.dismiss(data);
  } catch {
    // no modal open / already dismissed
    return false;
  }
}

  getIcon(title) {
    const name = title.toLowerCase();
    switch (name) {
      case 'home':
        return 'home-outline';
      case 'work':
        return 'briefcase-outline';
      default:
        return 'location-outline';
    }
  }

  async customStatusbar(primaryColor?: boolean) {
    if (Capacitor.getPlatform() != 'web') {
      await StatusBar.setStyle({
        style: primaryColor ? Style.Dark : Style.Light,
      });
      if (isPlatform('android'))
        StatusBar.setBackgroundColor({
          color: primaryColor ? '#de0f17' : '#ffffff',
        });
    }
  }

  async takePicture() {
    const image = await Camera.getPhoto({
      quality: 90,
      source: CameraSource.Prompt,
      width: 600,
      resultType: CameraResultType.Base64,
    });
    console.log(image);
    return image;
  }

  getBlob(b64Data) {
    let contentType = '';
    let sliceSize = 512;

    b64Data = b64Data.replace(/data\:image\/(jpeg|jpg|png)\;base64\,/gi, '');

    let byteCharacters = atob(b64Data);
    let byteArrays: any[] = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      let slice = byteCharacters.slice(offset, offset + sliceSize);

      let byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      let byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    let blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  checkPlatform(platform = 'web') {
    if (Capacitor.getPlatform() == platform) return true;
    return false;
  }

  async call(number) {
    return CallNumber.call({
      number,
      bypassAppChooser: this.checkPlatform('ios') ? true : false,
    });
  }

  navigateByUrl(url, replaceUrl = true) {
    if (replaceUrl == false) this.router.navigateByUrl(url);
    else this.router.navigateByUrl(url, { replaceUrl: replaceUrl });
  }

  navigate(url: any) {
    this.router.navigate(url);
  }

  isIos() {
    if (Capacitor.getPlatform() == 'ios') return true;
    return false;
  }

  formatNumber(num: any, fix_to: number = 2): number {
    const parsedNum = Number(num);
    if (isNaN(parsedNum)) {
      console.error(`Invalid number passed to formatNumber: ${num}`);
      return 0;
    }
    return parseFloat(parsedNum.toFixed(fix_to));
  }

  dateFormat(date, format) {
    return this.datePipe.transform(date, format);
  }

  deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  getDistanceFromLatLngInKm(lat1, lng1, lat2, lng2) {
    let radius = 6371;
    let lat = this.deg2rad(lat2 - lat1);
    let lng = this.deg2rad(lng2 - lng1);

    let result =
      Math.sin(lat / 2) * Math.sin(lat / 2) +
      Math.cos(this.deg2rad(lat1)) *
        Math.cos(this.deg2rad(lat2)) *
        Math.sin(lng / 2) *
        Math.sin(lng / 2);
    var c = 2 * Math.atan2(Math.sqrt(result), Math.sqrt(1 - result));
    var d = radius * c;
    return d;
  }

  playAudio() {
    NativeAudio.play({
      assetId: 'notification',
    });
  }
}
